import {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {useArrowNavigation} from '@pb/kit-hook-use-arrow-navigation';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

import type {StripOfPillsContextProps} from './strip-of-pills.context';
import type {Item} from './typedef';

const MaxShownPillsLength: number = 3;

type UseStripOfPillsReturn = {
    getParentProps: ReturnType<typeof useArrowNavigation>['getParentProps'],
    collapsed: boolean,
    setCollapsed: (collapsed: boolean) => void,
    canCollapse: boolean,
    context: StripOfPillsContextProps,
};

export const useStripOfPills = ({
    isCollapsed = true,
    isCollapsible = true,
    restoreOnBlur = true,
}): UseStripOfPillsReturn => {
    const [collapsed, _setCollapsed] = useState<boolean>(isCollapsible ? isCollapsed : false);
    const setCollapsed = useCallbackWithRef((newCollapsed: boolean) => {
        if (isCollapsible) {
            _setCollapsed(newCollapsed);
        }
    });
    const [items, setItems] = useState<Item[]>([]);
    const hiddenPills = useRef<Item[]>([]);

    useEffect(() => {
        setCollapsed(isCollapsed);
    }, [isCollapsed, setCollapsed]);

    const {
        context,
        getParentProps,
    } = useArrowNavigation({restoreOnBlur});

    const setHiddenPills = useCallbackWithRef((newItems: Item[]) => {
        hiddenPills.current = newItems;
        items.forEach(item => {
            item.setHidden(hiddenPills.current.includes(item));
        });
    });

    useLayoutEffect(() => {
        if (collapsed && items.length > MaxShownPillsLength) {
            setHiddenPills(items.slice(MaxShownPillsLength));
        } else {
            setHiddenPills([]);
        }
    }, [collapsed, items, items.length, setHiddenPills]);

    const contextValue = useMemo(() => ({
        addPill: (pill: Item) => {
            setItems(prevItemsList => {
                if (prevItemsList.includes(pill)) {
                    return prevItemsList;
                }
                return [...prevItemsList, pill];
            });
        },
        removePill: (pill: object) => {
            setItems(prevItemsList => prevItemsList.filter(item => item !== pill));
            setHiddenPills(hiddenPills.current.filter(item => item !== pill));
        },
        arrowNavigationContext: context,
    } as StripOfPillsContextProps), [context, setHiddenPills]);

    return {
        getParentProps,
        collapsed,
        setCollapsed,
        canCollapse: items.length > MaxShownPillsLength,
        context: contextValue,
    };
};
